// src/ui/VibeComments.jsx — one comment thread, used everywhere.
//
// 🟥 THERE WERE THREE OF THESE, BYTE FOR BYTE. The post pane, the comments pane
//    and the reels sheet each carried the same four lines of row markup, the
//    same empty state and the same composer — so replies, deletion and the
//    "@name" prefix would have had to be written three times and fixed three
//    times forever. The phone has exactly one comment renderer re-hosted in four
//    containers, and that is why threading it there was one change.
//
// 🟢 TWO LEVELS, NOT A TREE. A reply to a reply attaches to the same top-level
//    comment and carries the name it answered. The reason is in the header of
//    services/vibeComments.js, and the rule lives there rather than here so the
//    phone and the browser cannot disagree about it.
//
// ══ WHAT THE THREAD DOES NOW (all decided in services/vibeComments) ═════════
// • THE LINK. The blue "@name" on a reply is a link: click it and the thread
//   scrolls to the comment being answered and flashes it. `linkTargetOf` finds
//   the exact comment (`replyToId`), or the thread's top for replies written
//   before the link existed; gone → a line says so.
// • YOURS FIRST. `buildThreads(…, { pinUid })` leads with the reader's own
//   top-level comments — you always find what you said.
// • HIDDEN FOR HARM. The server stamps every comment; a harmful one is folded.
//   The reader sees "N comments hidden because they may be harmful · Show";
//   the author always sees their own, labelled. Same words as the phone
//   (modStrings, off the browser's language).
// • LANDING. A bell row carries `commentId`; `jumpTo` lands on it.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import SupporterBadge from './SupporterBadge';   // 💛 the Supporter badge, next to the name
import { Send, X, Trash, Verified, ThumbUp, ThumbUpFill, ThumbDown, ThumbDownFill, LinkIcon, Pin, Lock } from './icons';
import Pic from './VibeAvatar';
import {
  buildThreads, buildComment, commentsOf, canDeleteComment, replyCountOf, isOfficialComment,
  partitionComments, linkTargetOf, isCommentHidden, COMMENT_STR, COMMENT_MAX_LEN,
  pruneLocalComments, upsertComment, commentAge, LOCAL_STR,
  replyView, keepVisibleIds, replyToggleLabel,
  COMMENT_SORTS, DEFAULT_COMMENT_SORT, commentSortOf, SORT_STR, commentCountOf,
  threadIndent, threadRailX, threadElbowY, threadRailStartY, threadElbowW, THREAD_GEOM,
  reactionOf, reactionCounts, toggleReaction, commentLink, ACTION_STR,
  canCommentOn, canPinComment, isPostOwner, pinnedCommentIdOf, nextPinnedId, PIN_STR,
} from '../services/vibeComments';
import { reactVibeComment, pinVibeComment, commentPager, commentMine, useCommentMine } from '../services/vibeCommentApi';
import { shortCount } from '../services/postGrid';
/* 📄 COMMENTS A PAGE AT A TIME — documents now, not an array inside the post
   (which capped a post at a few thousand). What shows, when a skeleton shows
   and which old comments wait their turn: the phone's rules, generated. */
import { sheetComments, commentSheetPlan, isPagedPost } from '../services/commentPages';
import { useCommentPages } from '../services/useCommentPages';
import { OFFICIAL_PAGE_ID } from '../services/pageFollow';
import { mt } from '../services/modStrings';
/* 💬 "@someone" — the group chat's rules (services/mentions) and what a comment
   records about the people it names (services/commentMentions), both generated
   from the phone's TypeScript, so a name is blue on both clients or on neither */
import MentionPicker from './MentionPicker';
import { activeMentionQuery, applyMention, filterMentionMembers } from '../services/mentions';
import {
  commentSegments, mentionCandidates, mentionsForComment, mentionSearchTerm, MENTION_FLASH_MS,
} from '../services/commentMentions';
import { searchMentionPeople } from '../services/users';

const L = (k, params) => mt(k, undefined, params) || k;

/** The row's clock — "just now", 5m, 3h, 2d, then a date. English, like the rest of this client. */
export function ageLabel(ts, now) {
  const a = commentAge(ts, now);
  if (!a.ms) return '';
  if (a.unit === 'now') return L(LOCAL_STR.justNow);
  if (a.unit === 'date') return new Date(a.ms).toLocaleDateString([], { month: 'short', day: 'numeric' });
  return `${a.n}${a.unit}`;
}

/* 🌳 THE THREAD'S LINES, the twin of the phone's. Every one is pinned to the
   row's own top and bottom rather than given a height, so the line joining a
   one-word reply is short and the one joining a nine-line reply is long — the
   row measures itself and the rail follows it. The arithmetic (where a rail
   sits, how far a row is indented) is the shared rule's, so the two clients
   draw the same tree. */
function ThreadLines({ tree, rootHasChildren }) {
  if (!tree) {
    return rootHasChildren ? <span className="vb-thread-rail" style={{ left: threadRailX(0), top: threadRailStartY(0) }} /> : null;
  }
  const d = tree.depth;
  return (
    <>
      {tree.rails.map((on, i) => (on ? <span key={i} className="vb-thread-rail" style={{ left: threadRailX(i) }} /> : null))}
      <span
        className="vb-elbow"
        style={{ left: threadRailX(d - 1), width: threadElbowW(d), height: threadElbowY(d) }}
      />
      {!tree.isLast && <span className="vb-thread-rail" style={{ left: threadRailX(d - 1), top: threadElbowY(d) }} />}
      {tree.hasChildren && <span className="vb-thread-rail" style={{ left: threadRailX(d), top: threadRailStartY(d) }} />}
    </>
  );
}

/* 📄 one placeholder row — the avatar and two lines, the shape a comment takes */
function CommentSkeleton() {
  return (
    <div className="vb-cskel" aria-hidden="true">
      <i className="vb-cskel-pic" />
      <span><i className="vb-cskel-line short" /><i className="vb-cskel-line" /></span>
    </div>
  );
}

function CommentRow({ comment, isReply, tree, rootHasChildren, endsThread, post, me, myPageIds, onReply, onDelete, onPin, onJump, onRetry, onDiscard, onReact, onCopyLink, busy, flashing, allComments, onProfile }) {
  /* 🗑 the author, or the post's owner (theirs or anyone's) — the server holds the same rule */
  const canDelete = canDeleteComment({
    comment, post, viewerUid: me?.uid, myPageIds,
  });
  /* 📌 the post's owner pins one top-level comment — theirs or anyone's */
  const canPin = canPinComment({ comment, post, viewerUid: me?.uid, myPageIds });
  const isPinned = !isReply && !!comment.id && String(comment.id) === pinnedCommentIdOf(post);
  /* the owner still answers on a post they locked */
  const canReply = canCommentOn({ post, viewerUid: me?.uid, myPageIds });
  const hidden = isCommentHidden(comment);
  const mine = !!me?.uid && String(comment.userId) === String(me.uid);
  const local = comment._local || null;
  const react = { mine: reactionOf(comment, me?.uid), counts: reactionCounts(comment) };
  return (
    <div
      className={`vb-comment ${isReply ? 'reply' : ''} ${hidden ? 'hidden' : ''} ${flashing ? 'flash' : ''} ${local === 'posting' ? 'posting' : ''} ${local === 'failed' ? 'failed' : ''} ${endsThread ? 'ends' : ''}`}
      data-comment-id={comment.id}
      style={isReply ? { paddingLeft: threadIndent(tree ? tree.depth : 1) } : undefined}
    >
      <ThreadLines tree={tree} rootHasChildren={rootHasChildren} />
      {/* 🌳 THE SIZE IS THE RULE'S. Where a rail sits and where an elbow turns
          are both computed from the avatar's diameter — a browser drawing 24px
          avatars against geometry written for 28px puts every line off-centre
          by two pixels, which is exactly enough to look broken. */}
      <Pic src={comment.userImage} name={comment.userName} size={isReply ? THREAD_GEOM.replyAvatar : THREAD_GEOM.rootAvatar} />
      <div className="vb-comment-body">
        {/* 📌 the comment the post's owner pinned leads the thread, and says so */}
        {isPinned && (
          <span className="vb-comment-pinned"><Pin width={11} height={11} /> {L(PIN_STR.pinned)}</span>
        )}
        {/* ✔️ read off `pageId`, which only the server route writes — the portal
            answers comments as Allo, and an answer from Allo that looks like a
            comment from somebody who NAMED themselves Allo teaches people the
            name is the credential */}
        <b>
          {comment.userName}
          {!isOfficialComment(comment, OFFICIAL_PAGE_ID) && comment.userId ? <SupporterBadge uid={comment.userId} size={13} /> : null}
          {isOfficialComment(comment, OFFICIAL_PAGE_ID) && (
            <span className="official-badge" title="Official Allo account">
              <Verified width={13} height={13} />
            </span>
          )}
          {/* 💬 THE ROW'S OWN CLOCK: "Posting…" while the write is in flight,
              "Failed to post" when it did not land, the time otherwise —
              "just now" the moment the document has it. */}
          {local === 'posting' ? <time className="vb-comment-time posting">{L(LOCAL_STR.posting)}</time>
            : local === 'failed' ? <time className="vb-comment-time failed">{L(LOCAL_STR.failed)}</time>
              : <time className="vb-comment-time" dateTime={new Date(commentAge(comment.timestamp).ms || Date.now()).toISOString()} title={new Date(commentAge(comment.timestamp).ms || Date.now()).toLocaleString()}>{local === 'posted' ? L(LOCAL_STR.justNow) : ageLabel(comment.timestamp)}</time>}
        </b>
        <p>
          {/* 🟢 WHO THIS ANSWERED — IN BLUE, AND A LINK. With one indent for
              every reply, the name is the only thing separating an answer to the
              first reply from an answer to the fourth; clicking it goes to the
              words being answered. */}
          {isReply && comment.replyToName ? (
            <button
              type="button" className="vb-comment-at"
              onClick={(e) => { e.stopPropagation(); onJump(linkTargetOf(comment, allComments)); }}
              title={`Go to ${comment.replyToName}'s comment`}
            >@{comment.replyToName}</button>
          ) : null}
          {/* 🟢 THE SPACE IS THE SENTENCE'S, NOT THE LINK'S. It used to live
              inside the <button>, where HTML collapses it away at the element's
              edge — so every reply read "@AnnA reply." — and it also made the
              blank after the name clickable. */}
          {isReply && comment.replyToName ? ' ' : null}
          {/* 💬 "@SOMEONE" — BLUE, AND A WAY TO THEM. Drawn from what the comment
              recorded, so a word that merely starts with "@" stays a word. On the
              row a "mentioned you" landed on, MY name glows too. */}
          {commentSegments(comment).map((seg, i) => (seg.uid ? (
            <button
              key={`m${i}`} type="button"
              className={`vb-comment-at vb-mention ${flashing && String(seg.uid) === String(me?.uid) ? 'me' : ''}`}
              onClick={(e) => { e.stopPropagation(); if (onProfile) onProfile({ uid: String(seg.uid), name: String(seg.text).replace(/^@/, '') }); }}
              title={`Open ${String(seg.text).replace(/^@/, '')}'s profile`}
            >{seg.text}</button>
          ) : (
            <React.Fragment key={`t${i}`}>{seg.text}</React.Fragment>
          )))}
        </p>
        {/* 💬 the author's own folded comment says so — silently vanishing
            somebody's words is what makes them post them three more times */}
        {hidden && (
          <span className="vb-comment-hiddentag">{mine ? L(COMMENT_STR.yoursHidden) : L(COMMENT_STR.hiddenTag)}</span>
        )}
        {local === 'failed' ? (
          <div className="vb-comment-acts">
            {/* 🟥 THE WORDS ARE NOT LOST — the same comment is sent again, same id */}
            <button type="button" className="retry" onClick={() => onRetry(comment)}>{L(LOCAL_STR.retry)}</button>
            <button type="button" className="danger" onClick={() => onDiscard(comment)}><Trash width={12} height={12} /> Delete</button>
          </div>
        ) : local === 'posting' ? null : (
          <div className="vb-comment-acts">
            {/* 👍👎 ON EVERY ROW — a comment, a reply, a reply to a reply.
                🟢 BOTH WITH THEIR NUMBER, at any size: the ledger plus everybody
                   past it (services/vibeComments), written short — 1.2K, 100K —
                   the way the post's own likes are. */}
            <button
              type="button" className={`vb-react ${react.mine === 'like' ? 'on' : ''}`}
              onClick={() => onReact(comment, 'like')} title={L(ACTION_STR.like)} aria-label={L(ACTION_STR.like)} aria-pressed={react.mine === 'like'}
            >
              {react.mine === 'like' ? <ThumbUpFill width={13} height={13} /> : <ThumbUp width={13} height={13} />}
              {react.counts.likes > 0 ? <span>{shortCount(react.counts.likes)}</span> : null}
            </button>
            <button
              type="button" className={`vb-react down ${react.mine === 'dislike' ? 'on' : ''}`}
              onClick={() => onReact(comment, 'dislike')} title={L(ACTION_STR.dislike)} aria-label={L(ACTION_STR.dislike)} aria-pressed={react.mine === 'dislike'}
            >
              {react.mine === 'dislike' ? <ThumbDownFill width={13} height={13} /> : <ThumbDown width={13} height={13} />}
              {react.counts.dislikes > 0 ? <span>{shortCount(react.counts.dislikes)}</span> : null}
            </button>
            {canReply && (
              <button type="button" onClick={() => onReply(comment)}>Reply</button>
            )}
            {canPin && (
              <button type="button" className={isPinned ? 'on' : ''} disabled={busy} onClick={() => onPin(comment)} aria-pressed={isPinned}>
                <Pin width={11} height={11} /> {L(isPinned ? PIN_STR.unpin : PIN_STR.pin)}
              </button>
            )}
            {canDelete && (
              <button type="button" className="danger" disabled={busy} onClick={() => onDelete(comment)}>
                <Trash width={12} height={12} /> Delete
              </button>
            )}
            {/* 🔗 the link to THIS comment — a reply three levels down is
                otherwise "the third one, I think" */}
            <button type="button" className="vb-copylink" onClick={() => onCopyLink(comment)} title={L(ACTION_STR.copyLink)} aria-label={L(ACTION_STR.copyLink)}>
              <LinkIcon width={13} height={13} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function VibeComments({ post: postIn, me, myPages, onAdd, onDelete, busy, jumpTo, onProfile }) {
  /* 📌 THE PIN, AT ONCE. The owner's click shows before the server answers; the post's own value
     takes over the moment it changes (the snapshot caught up, or another device moved it). */
  const [pinOv, setPinOv] = useState(null);   // { postId, id }
  const livePin = postIn ? pinnedCommentIdOf(postIn) : null;
  useEffect(() => { setPinOv(null); }, [postIn?.id, livePin]);
  const post = useMemo(() => (postIn && pinOv && pinOv.postId === postIn.id ? { ...postIn, pinnedCommentId: pinOv.id } : postIn), [postIn, pinOv]);
  const [draft, setDraft] = useState('');
  /* 💬 "@" — the caret, the people picked for this draft, and what a search found */
  const inputRef = useRef(null);
  const [caret, setCaret] = useState(0);
  const pickedRef = useRef(new Map());
  const [found, setFound] = useState([]);
  const [replyingTo, setReplyingTo] = useState(null);
  /* 🟥 THE SEND LOCK IS READ AS WELL AS SET. Double-submitting a comment is one
     Enter keypress away — the form clears the box instantly, so the second press
     lands on an empty draft and returns early, but a fast paste-and-Enter twice
     does not. It also disables the button, which is the only visible sign that
     anything is happening on a slow connection. */
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState('');
  /* 💬 the fold, per mount — a new thread starts folded again */
  const [reveal, setReveal] = useState(false);
  const [flash, setFlash] = useState(null);
  const [note, setNote] = useState('');
  const listRef = useRef(null);
  /* 💬 THE COMMENTS I JUST WROTE, before the document has them — posting,
     posted (until the document catches up), or failed with Retry. Merged into
     the thread by id; the document's copy wins (the server keeps the id). */
  const [local, setLocal] = useState([]);
  const targets = useRef({});
  /* 💬 WHICH THREADS ARE OPEN. More than one reply folds behind "View N
     replies" — the rule is replyView's, so the phone folds the same threads. */
  const [openThreads, setOpenThreads] = useState([]);
  /* 💬 THE ORDER THE READER ASKED FOR. Lives here, with the thread, because the
     header lives here too — the three surfaces that host this component each
     used to print their own "Comments" label, which is three places to change
     one word and three chances for them to drift. */
  const [sort, setSort] = useState(DEFAULT_COMMENT_SORT);
  const [sortOpen, setSortOpen] = useState(false);
  /* 👍👎 THE THUMB THAT WAS JUST PRESSED, until the document carries it.
     🟥 DECLARED WITH THE OTHER HOOKS, NOT BESIDE THE FUNCTION THAT SETS IT.
        It used to be declared halfway down the component, below the `list` that
        reads it — and `const` is not hoisted like `var`: reading it first threw
        `Cannot access 'reacted' before initialization` DURING RENDER, so the
        whole page went blank the moment anybody opened a comment thread. The
        build could not warn: it is legal code, and the error only exists on the
        path where the read runs before the declaration — which is every path. */
  const [reacted, setReacted] = useState({});
  /* 📄 this post's pages — asked for when the thread mounts, topped up as the
     post's counter moves, kept by the pager after it unmounts */
  const pages = useCommentPages(post, sort);
  const moreRef = useRef(null);
  const toggleThread = (id) => setOpenThreads((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const openThread = (id) => { if (id) setOpenThreads((prev) => (prev.includes(id) ? prev : [...prev, id])); };

  const docList = commentsOf(post);
  /* 👍👎 past a comment's ledger, what the server says I pressed */
  useCommentMine(post?.id, [...docList, ...pages.items], me?.uid);
  const list = sheetComments(post, pages, { sort, local, pinUid: me?.uid })
    /* the thumb that was just pressed, until the document carries it */
    .map((c) => (reacted[String(c.id)] ? { ...c, ...reacted[String(c.id)] } : commentMine.view(post?.id, c)));
  useEffect(() => {
    /* the pages count as "the document" too — a comment is one now */
    setLocal((cur) => { const next = pruneLocalComments(cur, [...docList, ...pages.items]); return next.length === cur.length ? cur : next; });
  }, [docList.length, pages.items.length]); // eslint-disable-line react-hooks/exhaustive-deps
  const { visible, hiddenCount } = partitionComments(list, { viewerUid: me?.uid, reveal });
  const plan = commentSheetPlan({ post, state: pages, shown: list.length, offline: typeof navigator !== 'undefined' && navigator.onLine === false });
  const threads = buildThreads(visible, { pinUid: me?.uid, sort, pinnedId: pinnedCommentIdOf(post) });
  const myPageIds = (myPages || []).map((p) => String(p.id));
  /* 🔒 a locked post still takes its owner's words (the server lets them: commentRoutes.addComment) */
  const gate = { post, viewerUid: me?.uid, myPageIds };
  const canWrite = canCommentOn(gate);
  const ownerOnly = canWrite && post?.commentsEnabled === false && isPostOwner(gate);
  /* 📌 pin / un-pin — shown at once, put back if the server says no */
  const pin = async (c) => {
    if (!post?.id || !c?.id) return;
    const want = nextPinnedId(post, String(c.id));
    const pid = post.id;
    setPinOv({ postId: pid, id: want });
    const r = await pinVibeComment({ postId: pid, commentId: want });
    if (!r || !r.ok) {
      setPinOv(null);
      setNote(L(PIN_STR.pinFailed)); setTimeout(() => setNote(''), 2200);
      return;
    }
    if ((r.pinnedCommentId ?? null) !== want) setPinOv({ postId: pid, id: r.pinnedCommentId ?? null });
    /* the pinned comment itself, so a paged post that had not loaded it draws it at the top */
    if (r.comment) commentPager.absorb(pid, r.comment);
  };

  /* the link: scroll to the row and flash it; say so when it is gone */
  const jump = (target) => {
    if (!target || !target.id) { setNote(L(COMMENT_STR.replyGone)); setTimeout(() => setNote(''), 2200); return; }
    const id = String(target.id);
    /* 🟥 a reply the fold is hiding has no row to scroll to — open its thread first */
    if (target.parentId) openThread(String(target.parentId));
    const el = listRef.current && listRef.current.querySelector(`[data-comment-id="${id.replace(/"/g, '')}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    setFlash(id);
    /* two seconds — the length a landing from "mentioned you" is asked to glow */
    setTimeout(() => setFlash((cur) => (cur === id ? null : cur)), MENTION_FLASH_MS);
  };
  /* a bell row's landing — once the row exists */
  useEffect(() => {
    if (!jumpTo) return undefined;
    const t = setTimeout(() => {
      const target = list.find((c) => String(c.id || '') === String(jumpTo));
      /* 📄 on a page not loaded yet — ask for that one thread */
      if (!target && isPagedPost(post)) commentPager.focus(post, sort, String(jumpTo));
      if (target) {
        /* a folded target is shown for the landing — both kinds of fold */
        if (isCommentHidden(target) && String(target.userId) !== String(me?.uid)) setReveal(true);
        if (target.parentId) openThread(String(target.parentId));
        setTimeout(() => jump(target), 80);
      }
    }, 120);
    return () => clearTimeout(t);
  }, [jumpTo, list.length, pages.items.length]); // eslint-disable-line react-hooks/exhaustive-deps

  /* the write: the row is on screen before it, and stays on screen after a
     failure — as "Failed to post", with Retry — instead of vanishing */
  const send = async (comment, target) => {
    targets.current[comment.id] = target || null;
    setLocal((cur) => upsertComment(cur, { ...comment, _local: 'posting' }));
    try {
      const written = await onAdd(comment, target);
      setLocal((cur) => upsertComment(cur, { ...(written && typeof written === 'object' ? written : comment), _local: 'posted' }));
      /* 📄 …and onto the pages, which the post document no longer carries —
         without this the comment would vanish when "posted" is pruned */
      if (post?.id) commentPager.absorb(post.id, written && typeof written === 'object' ? written : comment);
    } catch (ex) {
      const code = String(ex?.code || '');
      /* 🟥 ONLY THE SERVER'S OWN WORDS ARE A REFUSAL. A Firestore error from the
         fallback write carries a code too ('unavailable', 'permission-denied')
         and is a failure to reach anything, not an answer. */
      const REFUSED = ['reply_target_gone', 'comments_off', 'too_long', 'empty', 'bad_request', 'not_found', 'unauthorized'];
      if (REFUSED.includes(code)) {
        /* a real answer from the server — the comment cannot be posted: say which */
        setLocal((cur) => cur.filter((c) => c.id !== comment.id));
        setErr(code === 'reply_target_gone' ? L(COMMENT_STR.replyGone) : code === 'comments_off' ? 'Comments are off for this post' : code === 'too_long' ? L(COMMENT_STR.tooLong) : 'That comment did not send.');
        setDraft(comment.content); setReplyingTo(target);
      } else {
        /* 🟥 nothing reached anything: the words stay, marked, with Retry */
        setLocal((cur) => upsertComment(cur, { ...comment, _local: 'failed' }));
      }
    }
  };
  /* 👍👎 the press shows at once and the server confirms; the rule computes
     both answers, so the optimistic one and the confirmed one cannot disagree
     (the state itself is declared with the other hooks, above — see the note) */
  const react = async (comment, kind) => {
    if (!me?.uid || !comment?.id || comment._local) return;
    const id = String(comment.id);
    const next = toggleReaction(comment, me.uid, kind);
    setReacted((cur) => ({ ...cur, [id]: next }));
    commentMine.press(post.id, id, next.myReaction);
    const thumbsOf = (c) => ({ likes: c.likes || [], dislikes: c.dislikes || [], likesExtra: c.likesExtra || 0, dislikesExtra: c.dislikesExtra || 0, myReaction: c.myReaction ?? null });
    try {
      const r = await reactVibeComment({ postId: post.id, commentId: id, kind });
      if (r.ok && r.comment) {
        commentMine.press(post.id, id, r.comment.myReaction ?? null);
        setReacted((cur) => ({ ...cur, [id]: thumbsOf(r.comment) }));
        if (post?.id && pages.items.some((c) => String(c.id) === id)) commentPager.absorb(post.id, r.comment);
      }
      else if (r.status) { commentMine.press(post.id, id, reactionOf(comment, me.uid)); setReacted((cur) => { const c2 = { ...cur }; delete c2[id]; return c2; }); }
    } catch {
      /* 🟥 a thumb that stayed pressed and was never recorded is worse than one
         that springs back — the row goes back to what the document says */
      commentMine.press(post.id, id, reactionOf(comment, me.uid));
      setReacted((cur) => { const c2 = { ...cur }; delete c2[id]; return c2; });
    }
  };
  const copyLink = async (comment) => {
    try {
      await navigator.clipboard.writeText(commentLink(post.id, comment.id));
      setNote(L(ACTION_STR.linkCopied)); setTimeout(() => setNote(''), 1800);
    } catch { /* nothing copied, nothing claimed */ }
  };

  const retry = (c) => { const { _local, ...plain } = c; send(plain, targets.current[c.id] || null); };
  const discard = (c) => { setLocal((cur) => cur.filter((x) => x.id !== c.id)); delete targets.current[c.id]; };

  /* 💬 "@": which run the caret is in, who it could mean, and the pick */
  const mentionQ = canWrite ? activeMentionQuery(draft, caret) : null;
  const mentionTerm = mentionQ ? mentionSearchTerm(mentionQ.query) : '';
  useEffect(() => {
    if (!mentionTerm) { setFound([]); return undefined; }
    let live = true;
    const t = setTimeout(() => { searchMentionPeople(mentionTerm).then((r) => { if (live) setFound(r); }).catch(() => {}); }, 220);
    return () => { live = false; clearTimeout(t); };
  }, [mentionTerm]);
  const mentionResults = mentionQ ? filterMentionMembers(mentionCandidates({
    post, comments: list, myUid: me?.uid,
    extra: [...Array.from(pickedRef.current.values()), ...found],
  }), mentionQ.query) : [];
  const readCaret = (el) => { if (el && typeof el.selectionStart === 'number' && el.selectionStart === el.selectionEnd) setCaret(el.selectionStart); };
  const pickMention = (m) => {
    const el = inputRef.current;
    const at = el && typeof el.selectionStart === 'number' ? el.selectionStart : caret;
    const q = activeMentionQuery(draft, at);
    if (!q) return;
    const next = applyMention(draft, q.start, at, m.name);
    pickedRef.current.set(m.uid, m);
    setDraft(next.text);
    setCaret(next.caret);
    /* the caret goes after the inserted name, once React has written the value */
    requestAnimationFrame(() => { try { el && el.focus(); el && el.setSelectionRange(next.caret, next.caret); } catch { /* fine */ } });
  };

  /* 📄 THE NEXT PAGE, ASKED FOR EARLY. A marker sits a few threads above the
     end; when it scrolls into view the next twenty are fetched, so the
     skeleton rows under the list are rarely seen at all. */
  useEffect(() => {
    const el = moreRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((en) => en.isIntersecting)) commentPager.more(post, sort);
    }, { rootMargin: '400px 0px' });
    io.observe(el);
    return () => io.disconnect();
  });

  const submit = async (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text || sending) return;
    if (text.length > COMMENT_MAX_LEN) { setErr(L(COMMENT_STR.tooLong)); return; }
    /* 🟥 THE TARGET IS FROZEN WITH THE TEXT. Both are cleared before the await
       so the box empties immediately; reading `replyingTo` afterwards would aim
       the reply at whatever the chip had become by the time the write returned. */
    const target = replyingTo;
    const comment = buildComment({
      me: { uid: me?.uid, name: me?.fullName || me?.username || 'Allo user', image: me?.profileImage || '' },
      text, replyingTo: target,
    });
    if (!comment) return;
    /* 💬 who the words name, of the people picked — re-derived on the server too */
    const mentioned = mentionsForComment(text, Array.from(pickedRef.current.values()), me?.uid);
    if (mentioned) Object.assign(comment, mentioned);
    pickedRef.current.clear();
    setDraft('');
    setReplyingTo(null);
    setSending(true); setErr('');
    try { await send(comment, target); } finally { setSending(false); }
  };

  return (
    <>
      {/* 💬 "33 comments", and the one control worth having beside it.
          🟥 NO ✕ AND NO ↻. Every surface that hosts this thread already has its
             own way out, and the thread is live — a refresh button on live data
             teaches people it is not. The phone's sheet says the same. */}
      <div className="vb-comment-head">
        <button
          type="button" className="vb-comment-sort" onClick={() => setSortOpen((v) => !v)}
          aria-haspopup="menu" aria-expanded={sortOpen} title={L(SORT_STR.title)}
        >
          <b>{L(SORT_STR.count, { count: commentCountOf(post) })}</b>
          {/* the same three rules the phone draws, from the same three numbers */}
          <svg width="18" height="14" viewBox="0 0 18 14" aria-hidden="true">
            <rect x="0" y="2" width="18" height="2" rx="1" fill="currentColor" />
            <rect x="0" y="6" width="12" height="2" rx="1" fill="currentColor" />
            <rect x="0" y="10" width="7" height="2" rx="1" fill="currentColor" />
          </svg>
        </button>
        {sortOpen && (
          <>
            {/* the click-away, so the menu closes like every other menu */}
            <button type="button" className="vb-sort-away" aria-label="Close" onClick={() => setSortOpen(false)} />
            <div className="vb-sort-menu" role="menu">
              {COMMENT_SORTS.map((k) => {
                const on = commentSortOf(sort) === k;
                return (
                  <button
                    key={k} type="button" role="menuitemradio" aria-checked={on}
                    className={`vb-sort-item ${on ? 'on' : ''}`}
                    onClick={() => { setSort(k); setSortOpen(false); }}
                  >
                    <span>{L(SORT_STR[k])}</span>
                    {on ? <span className="vb-sort-tick" aria-hidden="true">✓</span> : null}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
      <div className="vb-comment-list" ref={listRef}>
        {/* 📄 ONE RULE FOR THE SKELETON (commentSheetPlan): rows we have are
            shown at once; rows that exist and are not here yet are skeletons —
            as many as there are comments, up to six; no comments at all says so
            at once. */}
        {plan.mode === 'skeleton' && Array.from({ length: plan.rows }).map((_, i) => <CommentSkeleton key={`sk${i}`} />)}
        {(plan.mode === 'offline' || plan.mode === 'error') && (
          <div className="ct-empty subtle">
            <span>{plan.mode === 'offline' ? 'You are offline — comments will load when you are back.' : 'Comments did not load.'}</span>
            <button type="button" className="vb-more-retry" onClick={() => commentPager.retry(post, sort)}>Try again</button>
          </div>
        )}
        {plan.mode === 'empty' && hiddenCount === 0 && (
          <div className="ct-empty subtle"><span>No comments yet — say something nice.</span></div>
        )}
        {threads.map((th, ti) => {
          /* 💬 ONE REPLY IS SHOWN; MORE THAN ONE FOLDS. A reply still being
             sent, and the one a link just aimed at, are never folded — and a
             kept reply brings its ancestors with it.
             🌳 `view.shown` carries the shape of the conversation, which is
                what ThreadLines draws. */
          const tid = String(th.comment.id || '');
          const expanded = openThreads.includes(tid);
          const view = replyView(th.replies, { expanded, keepIds: keepVisibleIds(th.replies, { focusId: flash || jumpTo }) });
          const label = replyToggleLabel(view, expanded);
          return (
            <React.Fragment key={th.comment.id}>
              {ti === Math.max(0, threads.length - 5) && <span ref={moreRef} className="vb-more-mark" aria-hidden="true" />}
              <CommentRow
                comment={th.comment} isReply={false} rootHasChildren={view.rootHasChildren}
                endsThread={!view.shown.length && !label}
                post={post} me={me} myPageIds={myPageIds} busy={busy}
                onReply={(c) => { setReplyingTo(c); openThread(tid); }} onDelete={onDelete} onPin={pin} onJump={jump} onRetry={retry} onDiscard={discard}
                onReact={react} onCopyLink={copyLink}
                flashing={flash === String(th.comment.id)} allComments={list} onProfile={onProfile}
              />
              {view.shown.map((r, i) => (
                <CommentRow
                  key={r.comment.id} comment={r.comment} isReply tree={r}
                  endsThread={!label && i === view.shown.length - 1}
                  post={post} me={me} myPageIds={myPageIds} busy={busy}
                  onReply={(c) => { setReplyingTo(c); openThread(tid); }} onDelete={onDelete} onPin={pin} onJump={jump} onRetry={retry} onDiscard={discard}
                  onReact={react} onCopyLink={copyLink}
                  flashing={flash === String(r.comment.id)} allComments={list} onProfile={onProfile}
                />
              ))}
              {label && (
                <button
                  type="button" className="vb-replies-toggle ends" onClick={() => toggleThread(tid)} aria-expanded={expanded}
                  style={{ paddingLeft: threadIndent(1) - 8 }}
                >
                  <i style={{ left: threadRailX(0), width: threadIndent(1) - threadRailX(0) - 14 }} />
                  {L(label.key, { count: label.count })}
                </button>
              )}
            </React.Fragment>
          );
        })}
        {/* 📄 more on the way: a few skeleton rows, or Try again if the page failed */}
        {plan.footer > 0 && Array.from({ length: plan.footer }).map((_, i) => <CommentSkeleton key={`ft${i}`} />)}
        {plan.moreFailed && (
          <button type="button" className="vb-more-retry" onClick={() => commentPager.retry(post, sort)}>Load more comments</button>
        )}
        {/* 💬 HIDDEN FOR HARM — the count and the way to see them */}
        {hiddenCount > 0 && (
          <button type="button" className="vb-comment-hidden" onClick={() => setReveal((v) => !v)}>
            <span>{hiddenCount === 1 ? L(COMMENT_STR.hiddenOne) : L(COMMENT_STR.hiddenSome, { count: hiddenCount })}</span>
            <b>{reveal ? L(COMMENT_STR.hide) : L(COMMENT_STR.show)}</b>
          </button>
        )}
        {note && <div className="vb-comment-note">{note}</div>}
      </div>

      {/* 💬 the people to name, in a block just above the box (the chat composer's choice) */}
      {canWrite && mentionResults.length > 0 && (
        <MentionPicker results={mentionResults} onPick={pickMention} />
      )}
      {ownerOnly && (
        <div className="vb-cbar-owner"><Lock width={11} height={11} /> {L(PIN_STR.ownerOnly)}</div>
      )}
      {canWrite ? (
        <form className="vb-cbar" onSubmit={submit}>
          {/* 🟥 A REPLY THAT DOES NOT SAY WHO IT ANSWERS IS A COMMENT SENT TO THE
              WRONG PERSON. The chip is the only thing on screen between clicking
              Reply and sending, and it carries its own way out — without one the
              composer stays aimed at a comment that has scrolled away. */}
          {replyingTo && (
            <span className="vb-replychip">
              <span>{replyingTo.userName}</span>
              <button type="button" onClick={() => setReplyingTo(null)} aria-label="Cancel reply">
                <X width={11} height={11} />
              </button>
            </span>
          )}
          <input
            ref={inputRef}
            value={draft}
            placeholder={replyingTo ? `Reply to ${replyingTo.userName}…` : 'Write a comment…'}
            onChange={(e) => { setDraft(e.target.value); readCaret(e.target); if (err) setErr(''); }}
            onSelect={(e) => readCaret(e.target)}
            onKeyDown={(e) => {
              /* Enter takes the first name while the list is open, rather than sending half a mention */
              if (e.key === 'Enter' && mentionResults.length > 0) { e.preventDefault(); pickMention(mentionResults[0]); }
              else if (e.key === 'Escape' && mentionResults.length > 0) { e.preventDefault(); setCaret(-1); }
            }}
            maxLength={COMMENT_MAX_LEN + 50}
          />
          <button className="icon-btn" type="submit" aria-label="Send" disabled={sending}>
            <Send width={17} height={17} />
          </button>
          {err && <span className="vb-cbar-err" role="alert">{err}</span>}
        </form>
      ) : (
        <div className="vb-cbar off">Comments are off for this post</div>
      )}
    </>
  );
}

/** How many rows one delete takes with it — for the confirm wording. */
export const repliesUnder = replyCountOf;
